/**
 * Small pure 2D polygon helpers for dungeon dressing (sim yards, no three.js): convex clipping,
 * convex difference (A \ B as disjoint convex pieces), overlap tests and plane fitting.
 * All polygons are point arrays; convex routines expect counter-clockwise order.
 */
import type { Vec2 } from '@keystone/sim';

export type Pt = Vec2;
export type Poly = Pt[];

const EPS = 1e-7;

export function signedArea(p: readonly Pt[]): number {
  let s = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) s += (p[j]!.x * p[i]!.y - p[i]!.x * p[j]!.y);
  return s / 2;
}

export const area = (p: readonly Pt[]): number => Math.abs(signedArea(p));

export function ccw(p: readonly Pt[]): Poly {
  return signedArea(p) < 0 ? [...p].reverse() : [...p];
}

export function centroid(p: readonly Pt[]): Pt {
  let x = 0;
  let y = 0;
  for (const q of p) {
    x += q.x;
    y += q.y;
  }
  return { x: x / Math.max(1, p.length), y: y / Math.max(1, p.length) };
}

export function bbox(p: readonly Pt[]): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const q of p) {
    if (q.x < x0) x0 = q.x;
    if (q.y < y0) y0 = q.y;
    if (q.x > x1) x1 = q.x;
    if (q.y > y1) y1 = q.y;
  }
  return { x0, y0, x1, y1 };
}

export const rectPoly = (x0: number, y0: number, x1: number, y1: number): Poly => [
  { x: x0, y: y0 },
  { x: x1, y: y0 },
  { x: x1, y: y1 },
  { x: x0, y: y1 },
];

/** Cross product of (b - a) × (p - a): > 0 when p is left of a→b. */
const side = (a: Pt, b: Pt, p: Pt): number => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);

/** Keep the part of `poly` left of the directed line a→b (`keepLeft`) or right of it. */
export function clipHalfPlane(poly: readonly Pt[], a: Pt, b: Pt, keepLeft = true): Poly {
  const out: Poly = [];
  const n = poly.length;
  if (n === 0) return out;
  const s = (p: Pt): number => (keepLeft ? side(a, b, p) : -side(a, b, p));
  for (let i = 0; i < n; i++) {
    const P = poly[i]!;
    const Q = poly[(i + 1) % n]!;
    const sp = s(P);
    const sq = s(Q);
    if (sp >= -EPS) out.push(P);
    if ((sp > EPS && sq < -EPS) || (sp < -EPS && sq > EPS)) {
      const t = sp / (sp - sq);
      out.push({ x: P.x + (Q.x - P.x) * t, y: P.y + (Q.y - P.y) * t });
    }
  }
  return dedupe(out);
}

function dedupe(p: Poly): Poly {
  const out: Poly = [];
  for (const q of p) {
    const last = out[out.length - 1];
    if (!last || Math.abs(last.x - q.x) > 1e-6 || Math.abs(last.y - q.y) > 1e-6) out.push(q);
  }
  while (out.length > 1 && Math.abs(out[0]!.x - out[out.length - 1]!.x) < 1e-6 && Math.abs(out[0]!.y - out[out.length - 1]!.y) < 1e-6) out.pop();
  return out;
}

/** Intersection of two convex CCW polygons. */
export function clipConvex(subject: readonly Pt[], clip: readonly Pt[]): Poly {
  let out: Poly = [...subject];
  for (let i = 0; i < clip.length && out.length >= 3; i++) out = clipHalfPlane(out, clip[i]!, clip[(i + 1) % clip.length]!, true);
  return out.length >= 3 ? out : [];
}

/** Separating-axis overlap test for convex polygons (touching edges don't count). */
export function convexOverlap(A: readonly Pt[], B: readonly Pt[], eps = 1e-6): boolean {
  for (const P of [A, B]) {
    for (let i = 0; i < P.length; i++) {
      const a = P[i]!;
      const b = P[(i + 1) % P.length]!;
      const nx = -(b.y - a.y);
      const ny = b.x - a.x;
      let minA = Infinity;
      let maxA = -Infinity;
      let minB = Infinity;
      let maxB = -Infinity;
      for (const q of A) {
        const d = q.x * nx + q.y * ny;
        minA = Math.min(minA, d);
        maxA = Math.max(maxA, d);
      }
      for (const q of B) {
        const d = q.x * nx + q.y * ny;
        minB = Math.min(minB, d);
        maxB = Math.max(maxB, d);
      }
      const len = Math.hypot(nx, ny) || 1;
      if (maxA <= minB + eps * len || maxB <= minA + eps * len) return false;
    }
  }
  return true;
}

/**
 * A \ B for convex CCW polygons, as disjoint convex pieces (one per edge of B the remainder
 * crosses). Pieces smaller than `minArea` are dropped.
 */
export function convexDifference(A: readonly Pt[], B: readonly Pt[], minArea = 0.02): Poly[] {
  if (A.length < 3) return [];
  if (B.length < 3 || !convexOverlap(A, B)) return [[...A]];
  const out: Poly[] = [];
  let rem: Poly = [...A];
  for (let i = 0; i < B.length && rem.length >= 3; i++) {
    const a = B[i]!;
    const b = B[(i + 1) % B.length]!;
    const outside = clipHalfPlane(rem, a, b, false);
    if (outside.length >= 3 && area(outside) > minArea) out.push(outside);
    rem = clipHalfPlane(rem, a, b, true);
  }
  return out;
}

/** Subtract several convex polygons from a set of convex pieces. */
export function subtractAll(pieces: readonly Poly[], cutters: readonly Poly[], minArea = 0.02): Poly[] {
  let cur: Poly[] = pieces.map((p) => [...p]);
  for (const c of cutters) {
    const next: Poly[] = [];
    for (const p of cur) next.push(...convexDifference(p, c, minArea));
    cur = next;
  }
  return cur;
}

export function pointInConvex(p: Pt, poly: readonly Pt[], eps = 1e-9): boolean {
  for (let i = 0; i < poly.length; i++) if (side(poly[i]!, poly[(i + 1) % poly.length]!, p) < -eps) return false;
  return poly.length >= 3;
}

export function pointInPoly(p: Pt, poly: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const l2 = vx * vx + vy * vy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + vx * t), p.y - (a.y + vy * t));
}

/** Distance from p to a polygon's boundary (0 inside when `solid`). */
export function distToPoly(p: Pt, poly: readonly Pt[], solid = true): number {
  if (solid && pointInPoly(p, poly)) return 0;
  let d = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) d = Math.min(d, distToSegment(p, poly[j]!, poly[i]!));
  return d;
}

/** Offset a convex CCW polygon outward by `d` (mitre joins). */
export function offsetConvex(poly: readonly Pt[], d: number): Poly {
  const n = poly.length;
  const out: Poly = [];
  for (let i = 0; i < n; i++) {
    const prev = poly[(i - 1 + n) % n]!;
    const cur = poly[i]!;
    const next = poly[(i + 1) % n]!;
    const n1 = outwardNormal(prev, cur);
    const n2 = outwardNormal(cur, next);
    const bx = n1.x + n2.x;
    const by = n1.y + n2.y;
    const k = 1 + n1.x * n2.x + n1.y * n2.y;
    out.push({ x: cur.x + (bx * d) / Math.max(k, 0.2), y: cur.y + (by * d) / Math.max(k, 0.2) });
  }
  return out;
}

/** Outward unit normal of a CCW polygon edge a→b (to its right). */
export function outwardNormal(a: Pt, b: Pt): Pt {
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return { x: (b.y - a.y) / len, y: -(b.x - a.x) / len };
}

/** Plane through a polygon's vertices with heights `h` (least squares on 3 spread vertices). */
export interface Plane {
  a: number;
  b: number;
  c: number;
}
export function fitPlane(poly: readonly Pt[], h: readonly number[]): Plane {
  // Pick three vertices with the largest triangle for stability.
  let best = 0;
  let tri: [number, number, number] = [0, 1, 2];
  const n = poly.length;
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++)
      for (let k = j + 1; k < n; k++) {
        const ar = Math.abs(side(poly[i]!, poly[j]!, poly[k]!));
        if (ar > best) {
          best = ar;
          tri = [i, j, k];
        }
      }
  const [i, j, k] = tri;
  const P = poly[i]!;
  const Q = poly[j]!;
  const R = poly[k]!;
  const det = (Q.x - P.x) * (R.y - P.y) - (R.x - P.x) * (Q.y - P.y);
  if (Math.abs(det) < 1e-9) return { a: 0, b: 0, c: h[0] ?? 0 };
  const dh1 = h[j]! - h[i]!;
  const dh2 = h[k]! - h[i]!;
  const a = (dh1 * (R.y - P.y) - dh2 * (Q.y - P.y)) / det;
  const b = ((Q.x - P.x) * dh2 - (R.x - P.x) * dh1) / det;
  return { a, b, c: h[i]! - a * P.x - b * P.y };
}
export const planeAt = (pl: Plane, x: number, y: number): number => pl.a * x + pl.b * y + pl.c;
